import { Link } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { useLocale } from '../hooks/useLocale';
import { formatPrice as fmtPrice } from '../utils/format';
import { tField } from '../utils/i18nContent';
import { thumbnail as proxyThumb } from '../lib/imageProxy';

const CartPage = () => {
  const { items, totalItems, removeFromCart, getTotalPrice } = useCart();
  const locale = useLocale();
  const { t } = useTranslation('cart');
  const formatPrice = (price) => fmtPrice(price, 'MXN', locale);

  return (
    <div className="container cart-page" style={{ padding: '2rem 0', width: '100%' }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%'
      }}>
        <h1 style={{
          marginBottom: '1.5rem',
          fontSize: '1.75rem',
          color: '#333',
          fontFamily: "'Gambado Sans', sans-serif"
        }}>
          {t('title')}
        </h1>

        <div className="cart-grid" style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '2rem',
          alignItems: 'start'
        }}>
          <div>
            {items.length === 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '3rem 1rem'
              }}>
                <ShoppingBag
                  size={48}
                  color="#d0d0d0"
                  style={{ marginBottom: '1rem' }}
                />
                <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1rem' }}>
                  {t('empty')}
                </p>
                <Link
                  to="/"
                  style={{
                    color: '#2ca9e1',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '500'
                  }}
                >
                  {t('viewProducts')}
                </Link>
              </div>
            )}

            {items.map(item => {
              const displayName = tField(item, 'name', locale) || item.name;
              const variantLabel = item.variantId
                ? ((locale.startsWith('en') && item.variantName_en) ? item.variantName_en : item.variantName)
                : null;
              return (
              <div key={`${item.source || 'product'}-${item.id}-${item.variantId || ''}`} className="cart-item">
                <img
                  src={proxyThumb(item.variantImage || item.image)}
                  alt={displayName}
                  loading="lazy"
                  className="cart-item-image"
                  style={{
                    width: item.id === 4 ? '50px' : '80px',
                    height: item.id === 4 ? '50px' : '80px',
                    objectFit: item.id === 4 ? 'contain' : 'cover',
                    background: item.id === 4 ? 'white' : 'transparent'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/80x80/f8f9fa/333?text=Product';
                  }}
                />

                <div className="cart-item-details">
                  <h3 className="cart-item-name">{displayName}</h3>
                  {variantLabel && (
                    <span style={{ fontSize: '0.82rem', color: '#666' }}>{t('color')}: {variantLabel}</span>
                  )}
                </div>

                <div style={{ textAlign: 'right' }}>
                  {item.isPreorder && (
                    <span style={{ fontSize: '0.75rem', color: '#6c757d', display: 'block', marginBottom: '0.25rem' }}>
                      {t('deposit')}
                    </span>
                  )}
                  <p style={{ fontSize: '1.25rem', fontWeight: '600', color: '#2ca9e1' }}>
                    {formatPrice(item.price)}
                  </p>
                  <button
                    onClick={() => removeFromCart(item.id, item.source, item.variantId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      marginTop: '0.5rem'
                    }}
                    title={t('removeItem')}
                    aria-label={t('removeItem')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
            })}
          </div>

          <div
            className="cart-total"
            style={{
              background: '#f8f9fa',
              boxShadow: 'none',
              border: '1px solid #eee',
              textAlign: 'left'
            }}
          >
            <h3 style={{ fontFamily: "'Gambado Sans', sans-serif" }}>
              {t('summary.heading')}
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span>{t('summary.items', { count: totalItems })}</span>
                <span>{formatPrice(getTotalPrice())}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span>{t('summary.shipping')}</span>
                <span>{t('summary.shippingPlaceholder')}</span>
              </div>
              <hr style={{ margin: '1rem 0', border: '1px solid #eee' }} />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: '600'
              }}>
                <span>{t('summary.subtotal')}</span>
                <span>{formatPrice(getTotalPrice())}</span>
              </div>
            </div>

            <Link
              to="/checkout"
              style={{
                display: 'block',
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#2ca9e1',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                textAlign: 'center',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              {t('checkout')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
